import type { AuthPort } from '../ports/auth_port'
import { LoginUseCase } from '../usercases/login_usecase'
import { AuthRepository } from '@/features/signin/infrastructure/repository/auth_repository'
import { AppService } from '@/core/http/http-client'
import { ApiError } from '@/core/http/response_error'
import type { Credential } from '@/features/signin/domains/credential'
import type { ResponseResult } from '@/core/http/response-result'
import type { AuthUserToken } from '../../domains/auth_user_token'
import type { AuthUser } from '../../domains/auth_user'
import { MeUseCase } from '../usercases/me_usecase'
import { LogoutUseCase } from '../usercases/logout_usecase'

class AuthService {
  private authAdapter: AuthPort
  private loginUseCase: LoginUseCase
  private meUseCase: MeUseCase
  private logoutUseCase: LogoutUseCase

  constructor() {
    const http = new AppService()
    this.authAdapter = new AuthRepository(http)
    this.loginUseCase = new LoginUseCase(this.authAdapter)
    this.meUseCase = new MeUseCase(this.authAdapter)
    this.logoutUseCase = new LogoutUseCase(this.authAdapter)
  }

  async login(payload: {
    email: string
    password: string
  }): Promise<ResponseResult<AuthUserToken>> {
    try {
      const credential: Credential = {
        user: payload.email,
        password: payload.password,
      }
      const result = await this.loginUseCase.execute(credential)
      return result
    } catch (error) {
      const _error: ApiError = error as ApiError
      return {
        STATUS_CODE: 2,
        ERROR: _error,
      }
    }
  }

  async me(token: string): Promise<ResponseResult<AuthUser>> {
    try {
      const result = await this.meUseCase.execute(token)
      return result
    } catch (error) {
      const _error: ApiError = error as ApiError
      return {
        STATUS_CODE: 2,
        ERROR: _error,
      }
    }
  }

  async logout(): Promise<void> {
    await this.logoutUseCase.execute()
  }
}

export const authService = new AuthService()
